import { type TFunction } from 'i18next';
import {
  type FormattedPrice,
  type PriceOfferType,
  type PriceParts,
} from '../services/appointment/model';
import {
  type CurrencyDecimals,
  type CurrencyTransition,
} from '../types/CSS_CONFIG';

// Implementation of rounding numbers taken from Edit 3 solution at
// https://stackoverflow.com/a/15762794
const roundValue = (value: number, nbrOfDecimals: number): number => {
  const factor = 10 ** nbrOfDecimals;
  const n = parseFloat((value * factor).toFixed(11));
  const result = Math.round(n) / factor;
  return +result.toFixed(nbrOfDecimals);
};

const DECIMAL_RULES = {
  WHEN_NO_DECIMAL: {
    SHOW_NONE: undefined,
    SHOW_DASH: '-',
    SHOW_TWO: '00',
  },
  WHEN_ONE_DECIMAL: {
    SHOW_ONE: (value: string) => value,
    SHOW_TWO: (value: string) => value.padEnd(2, '0'),
  },
};

const getDecimalValue = (
  decimalConfig: CurrencyDecimals,
  decimalValue: string,
): string | undefined => {
  const {
    displayRuleWhenNoDecimalInPrice,
    displayRuleWhenOneDecimalInPrice,
    isZeroDecimalCurrency,
  } = decimalConfig;

  if (isZeroDecimalCurrency) {
    return undefined;
  }

  const decimalValueAsNumber = Number(decimalValue);
  if (decimalValueAsNumber === 0) {
    return DECIMAL_RULES.WHEN_NO_DECIMAL[
      displayRuleWhenNoDecimalInPrice || 'SHOW_TWO'
    ];
  }

  const nbrOfDecimalPlaces = decimalValue.length;
  if (nbrOfDecimalPlaces === 1) {
    const ruleFunc =
      DECIMAL_RULES.WHEN_ONE_DECIMAL[
        displayRuleWhenOneDecimalInPrice || 'SHOW_TWO'
      ];
    return ruleFunc(decimalValue);
  }

  return decimalValue;
};

const getIntegerValueWithSeparator = (
  value: string,
  thousandsSeparator: string,
) => {
  const groupsOfThousands = [];
  const nbrOfDigits = value.length;

  for (let i = nbrOfDigits - 1; i >= 0; i -= 3) {
    const startIndexOfGroup = Math.max(0, i - 2);
    const endIndexOfGroup = i + 1;
    const group = value.slice(startIndexOfGroup, endIndexOfGroup);
    groupsOfThousands.unshift(group);
  }

  return groupsOfThousands.join(thousandsSeparator);
};

const adjustPriceOnWrongDecimalCount = (
  price: number,
  decimalConfig?: CurrencyDecimals,
) => {
  const { isZeroDecimalCurrency } = decimalConfig || {};
  const maxDecimalPlaces = isZeroDecimalCurrency ? 0 : 2;
  const [, decimalPart] = price.toString().split('.');
  if (!decimalPart || decimalPart.length <= maxDecimalPlaces) {
    return price;
  }
  return roundValue(price, maxDecimalPlaces);
};

const getPriceParts = (
  price: number,
  appConfig: typeof CSS_CONFIG,
  isNewCurrency = false,
): PriceParts => {
  const {
    currency,
    decimals = {},
    separators,
  } = isNewCurrency
    ? appConfig.prices?.currencyTransition?.newCurrencyFormat || {}
    : appConfig.prices?.format || {};

  const priceWithCorrectDecimals = adjustPriceOnWrongDecimalCount(
    price,
    decimals,
  );
  const [integerPart, decimalPart] = priceWithCorrectDecimals
    .toString()
    .split('.');

  const integerValue = separators?.thousandsPart
    ? getIntegerValueWithSeparator(integerPart, separators.thousandsPart)
    : integerPart;

  const decimalValue = getDecimalValue(decimals, decimalPart || '0');

  const currencyParts = currency
    ? {
        currencyLabel:
          currency.hideOnDecimalValue && decimalValue ? '' : currency.sign,
        currencyPosition: currency.position,
        currencySpacing: currency.spacing,
        currencyVerticalAlignment: currency.verticalAlignment,
      }
    : {};

  return {
    ...currencyParts,
    decimalSign: separators?.decimalPart || '.',
    decimalValue,
    decimalVerticalAlignment: decimals?.verticalAlignment || 'super',
    integerValue,
  };
};

type Operator = 'DIV' | 'MUL';

const convertCurrency = (
  value: number,
  rate: number,
  operator: Operator,
  decimalPlaces: number,
): number => {
  const result = operator === 'DIV' ? value / rate : value * rate;
  return roundValue(result, decimalPlaces);
};

const getConversionSpecifics = (
  currencyCode: string,
  currencyTransition: CurrencyTransition,
) => {
  const { newCurrency } = currencyTransition;
  const isPriceCurrencyNewCurrency = currencyCode === newCurrency.currencyCode;
  const operator: Operator = isPriceCurrencyNewCurrency ? 'MUL' : 'DIV';

  return {
    isPriceCurrencyNewCurrency,
    operator,
  };
};

const getDualFormattedPrice = (
  price: number,
  currencyCode: string,
  currencyTransition: CurrencyTransition,
  appConfig: typeof CSS_CONFIG,
): FormattedPrice => {
  const {
    exchangeRateNewToOld,
    oldCurrency,
    newCurrency,
    currencyDisplayOrder,
  } = currencyTransition;
  const { operator, isPriceCurrencyNewCurrency } = getConversionSpecifics(
    currencyCode,
    currencyTransition,
  );
  const convertedPrice = convertCurrency(
    price,
    exchangeRateNewToOld,
    operator,
    isPriceCurrencyNewCurrency
      ? oldCurrency.decimalPlaces
      : newCurrency.decimalPlaces,
  );
  const newPrice = isPriceCurrencyNewCurrency ? price : convertedPrice;
  const oldPrice = isPriceCurrencyNewCurrency ? convertedPrice : price;

  const mainPrice =
    currencyDisplayOrder === 'NEW_CURRENCY_AS_MAIN_PRICE'
      ? getPriceParts(newPrice, appConfig, true)
      : getPriceParts(oldPrice, appConfig);
  const secondaryPrice =
    currencyDisplayOrder === 'NEW_CURRENCY_AS_MAIN_PRICE'
      ? getPriceParts(oldPrice, appConfig)
      : getPriceParts(newPrice, appConfig, true);
  return {
    mainPrice,
    secondaryPrice,
  };
};

export const getFormattedPrice = (
  price: number,
  appConfig: typeof CSS_CONFIG,
  currencyCode?: string,
): FormattedPrice => {
  const { currencyTransition } = appConfig.prices || {};
  if (currencyTransition && currencyTransition.enabled && currencyCode) {
    return getDualFormattedPrice(
      price,
      currencyCode,
      currencyTransition,
      appConfig,
    );
  }

  return {
    mainPrice: getPriceParts(price, appConfig),
  };
};

export const isIkeaMembershipSavings = (
  priceOfferType?: PriceOfferType,
): boolean => priceOfferType === 'family' || priceOfferType === 'ibn';

export const isNumericString = (value: string): boolean => {
  const trimmedValue = value.trim();
  return /\d/.test(trimmedValue);
};

export const shouldHidePriceZero = (
  priceOfferType: PriceOfferType | undefined,
  priceExclSavings: number,
  appConfig: typeof CSS_CONFIG,
): boolean => {
  return (
    !priceOfferType &&
    priceExclSavings === 0 &&
    !!appConfig.prices?.hideRegularPriceZero
  );
};

export const getPriceIntegerValue = (price: PriceParts | string): string => {
  if (typeof price === 'string') {
    return price;
  }
  return price.integerValue || '';
};

export const getFreeOrZeroPrice = (
  priceAmount: number,
  formattedPrice: PriceParts,
  freeTranslationKey: string,
  appConfig: typeof CSS_CONFIG,
  t: TFunction,
): PriceParts => {
  if (priceAmount > 0) {
    return formattedPrice;
  }

  if (appConfig.prices?.showFreePriceAsZero && priceAmount === 0) {
    return formattedPrice;
  }

  return { integerValue: t(freeTranslationKey) };
};
